import { Mic, FileText, Compass, MessageCircleQuestion, Keyboard, UserPlus, FileSignature, Receipt, UserMinus, Palmtree, Gift, Stethoscope, Clock, SlidersHorizontal,
  FolderOpen, MessagesSquare, Sparkles, Check, Monitor, Users, Workflow, Smartphone, LayoutDashboard, ListChecks, CalendarCheck2, Globe, ShieldCheck } from "lucide-react";
import { Container, Eyebrow, H2, Reveal, PrimaryCta, DemoCta, Price, PLAN_NOTE } from "./ui";
import { LeadForm } from "./LeadDialog";

const JESSY = [[Mic, "Assistência por voz"], [FileText, "Apoio no preenchimento de formulários"], [Compass, "Orientação durante os processos"],
  [MessageCircleQuestion, "Consultas e informações conforme as funcionalidades da plataforma"], [Keyboard, "Menos digitação e retrabalho"]];
const STEP_IMG = [["/step1.webp", "Você fala"], ["/step2.webp", "A Jessy preenche"], ["/step3.webp", "Você confere e confirma"]];

export function Jessy() {
  return (
    <section id="jessy" className="relative overflow-hidden bg-[#063C6D] py-20 sm:py-28 text-white scroll-mt-20" data-testid="lp-jessy">
      <div className="pointer-events-none absolute inset-0 lp-grid-dark" />
      <Container className="relative grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <Eyebrow light>Inteligência Artificial</Eyebrow>
          <H2 light>Conheça a Jessy, sua assistente de Departamento Pessoal com IA.</H2>
          <p className="mt-6 text-lg leading-relaxed text-sky-50/85">Com a Jessy, sua empresa ganha mais agilidade nas rotinas de Departamento Pessoal. A Inteligência Artificial auxilia no preenchimento de informações, na organização das solicitações e na utilização da plataforma, tornando os processos mais simples e intuitivos.</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="lp-jessy-features">{JESSY.map(([I, t]) => (
            <li key={t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-[15px]"><I className="mt-0.5 h-5 w-5 shrink-0 text-sky-300" />{t}</li>))}</ul>
          <p className="mt-5 text-xs text-sky-100/60" data-testid="lp-jessy-note">A Jessy auxilia nas rotinas: você revisa as informações antes de enviar, e a equipe DPdigi realiza a conferência e o processamento.</p>
          <div className="mt-8"><PrimaryCta source="jessy" label="QUERO CONHECER A JESSY" testid="lp-jessy-cta" light /></div>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-6 grid gap-3">{STEP_IMG.map(([src, t], i) => (
          <div key={t} className="flex items-center gap-4 rounded-3xl bg-white p-4 text-[#0F172A] shadow-2xl shadow-black/20">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0B6FC2] font-bold text-white">{i + 1}</span>
            <img src={src} alt="" className="h-20 w-24 rounded-xl object-contain" loading="lazy" />
            <span className="font-display text-xl font-extrabold text-[#063C6D]">{t}</span>
          </div>))}</Reveal>
      </Container>
    </section>);
}

const SERVICES = [
  [UserPlus, "Admissão digital", "Processo de admissão organizado digitalmente, com coleta de dados e documentos."],
  [FileSignature, "Contratos digitais", "Contratos de trabalho digitais com assinatura pelo celular."],
  [Receipt, "Folha de pagamento", "Envio digital das movimentações, processamento pela equipe DPdigi e disponibilização dos documentos."],
  [UserMinus, "Desligamentos", "Solicitações e acompanhamento dos processos de desligamento."],
  [Palmtree, "Férias", "Controle de períodos aquisitivos, planejamento e acompanhamento das férias dos funcionários."],
  [Gift, "Benefícios", "Gestão de vale-transporte, vale-refeição, alimentação e demais benefícios."],
  [Stethoscope, "Afastamentos e atestados", "Registro e acompanhamento de afastamentos e atestados."],
  [Clock, "Movimentações mensais", "Controle de horas extras, adicional noturno, faltas, comissões e outras ocorrências."],
  [SlidersHorizontal, "Alterações contratuais", "Solicitações de alterações salariais, cargos, jornadas e informações cadastrais."],
  [FolderOpen, "Documentos digitais", "Organização de documentos, históricos e arquivos."],
  [MessagesSquare, "Solicitações online", "Acompanhamento das demandas enviadas à equipe DPdigi."],
  [Sparkles, "Inteligência Artificial", "Assistência digital com a Jessy AI."],
];

export function Services() {
  return (
    <section id="servicos" className="bg-[#F8FAFC] py-20 sm:py-28 scroll-mt-20" data-testid="lp-services">
      <Container>
        <Reveal className="max-w-2xl"><Eyebrow>Serviços</Eyebrow><H2>Tudo o que sua empresa precisa em um único lugar.</H2></Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{SERVICES.map(([I, t, d], i) => (
          <Reveal key={t} delay={(i % 4) * 60} className="group rounded-2xl border border-slate-200 bg-white p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-[#0B6FC2]/40 hover:shadow-lg hover:shadow-slate-200/70">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E3EEFF] text-[#0B6FC2] transition-colors group-hover:bg-[#0B6FC2] group-hover:text-white"><I className="h-5 w-5" /></span>
            <h3 className="mt-5 font-display text-lg font-semibold text-[#0F172A]" data-testid={`lp-service-${i}`}>{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </Reveal>))}</div>
        <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3"><PrimaryCta source="services" testid="lp-services-cta" /><DemoCta testid="lp-services-demo" /></div>
      </Container>
    </section>);
}

const AUTO = ["Processos digitais", "Informações centralizadas", "Redução de tarefas manuais", "Histórico organizado", "Acompanhamento das solicitações", "Comunicação com a equipe DPdigi"];

export function Automation() {
  return (
    <section id="automacao" className="bg-white py-20 sm:py-28 scroll-mt-20" data-testid="lp-automation">
      <Container className="grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <Eyebrow>Automação</Eyebrow>
          <H2>Menos burocracia.<br /><span className="text-[#0B6FC2]">Mais produtividade.</span></H2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">A DPdigi conecta as informações dos funcionários, documentos, solicitações e movimentações em uma única plataforma.</p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">Os processos digitais reduzem atividades repetitivas, facilitam a comunicação e permitem que sua empresa acompanhe cada etapa com mais organização e agilidade.</p>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-6 rounded-[2rem] border border-[#0B6FC2]/15 bg-[#F0F7FF] p-6 sm:p-8">
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="lp-automation-list">{AUTO.map((t) => (
            <li key={t} className="flex items-center gap-3 rounded-xl bg-white px-4 py-3.5 text-[15px] font-medium text-[#063C6D] shadow-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0B6FC2] text-white"><Check className="h-3.5 w-3.5" /></span>{t}</li>))}</ul>
        </Reveal>
      </Container>
    </section>);
}

const STEPS = [["Cadastre sua empresa", "A empresa recebe acesso à plataforma DPdigi."], ["Gerencie seus funcionários", "Cadastre funcionários e acompanhe as informações trabalhistas."],
  ["Envie suas solicitações", "Informe admissões, desligamentos, férias, benefícios e movimentações pela plataforma."],
  ["A DPdigi processa", "A equipe especializada realiza os processamentos contratados e disponibiliza os resultados e documentos correspondentes."]];

export function HowItWorks() {
  return (
    <section id="como-funciona" className="bg-[#F8FAFC] py-20 sm:py-28 scroll-mt-20" data-testid="lp-how">
      <Container>
        <Reveal className="max-w-3xl"><Eyebrow>Como funciona</Eyebrow><H2>Simples para sua empresa.<br /><span className="text-[#0B6FC2]">Completo para seu Departamento Pessoal.</span></H2></Reveal>
        <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">{STEPS.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={i * 80} className="relative rounded-2xl border border-slate-200 bg-white p-6" data-testid={`lp-how-step-${i + 1}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#063C6D] font-display text-lg font-bold text-white">{i + 1}</div>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#0B6FC2]">Etapa {i + 1}</div>
            <h3 className="mt-1 font-display text-lg font-semibold text-[#0F172A]">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </Reveal>))}</ol>
      </Container>
    </section>);
}

const DIFF = [[Monitor, "Departamento Pessoal digital"], [Sparkles, "Inteligência Artificial integrada"], [Users, "Equipe especializada"], [Workflow, "Processos automatizados"],
  [Smartphone, "Assinatura digital pelo celular"], [LayoutDashboard, "Gestão centralizada"], [ListChecks, "Acompanhamento das solicitações"], [CalendarCheck2, "Mensalidade previsível"], [Globe, "Plataforma acessível pela internet"]];

export function Differentials() {
  return (
    <section id="diferenciais" className="bg-white py-20 sm:py-28 scroll-mt-20" data-testid="lp-diff">
      <Container>
        <Reveal className="max-w-2xl"><Eyebrow>Diferenciais</Eyebrow><H2>Por que escolher a DPdigi?</H2></Reveal>
        <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{DIFF.map(([I, t], i) => (
          <Reveal as="li" key={t} delay={(i % 3) * 60} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#F8FAFC] px-5 py-5 transition-[border-color] duration-200 hover:border-[#0B6FC2]/40">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#063C6D] text-white"><I className="h-5 w-5" /></span>
            <span className="font-display text-[17px] font-semibold text-[#0F172A]">{t}</span>
          </Reveal>))}</ul>
      </Container>
    </section>);
}

export function PriceSection() {
  return (
    <section id="planos" className="bg-white pb-20 sm:pb-28 scroll-mt-20" data-testid="lp-price">
      <Container>
        <Reveal className="relative overflow-hidden grid lg:grid-cols-12 gap-10 items-center rounded-[2rem] bg-[#0B6FC2] p-8 sm:p-12 lg:p-16 text-white shadow-2xl shadow-[#0B6FC2]/30">
          <div className="pointer-events-none absolute inset-0 lp-grid-dark" />
          <div className="relative lg:col-span-7">
            <Eyebrow light>Planos</Eyebrow>
            <H2 light>Seu Departamento Pessoal completo por muito menos do que você imagina.</H2>
            <p className="mt-6 text-lg leading-relaxed text-sky-50/90">Uma solução que reúne tecnologia, automação e processamento das rotinas de Departamento Pessoal.</p>
          </div>
          <div className="relative lg:col-span-5 rounded-3xl bg-[#063C6D] p-8 shadow-2xl shadow-black/20">
            <Price light size="xl" testid="lp-price-big" />
            <div className="mt-8"><PrimaryCta source="price" label="CONHECER OS PLANOS" testid="lp-price-cta" light className="w-full" /></div>
            <p className="mt-5 text-[12px] leading-relaxed text-sky-100/75" data-testid="lp-price-note">*{PLAN_NOTE}</p>
          </div>
        </Reveal>
      </Container>
    </section>);
}

const LeadHead = () => (
  <div><div className="font-display text-2xl font-bold text-[#0F172A]">Receba uma proposta</div><p className="mt-1 text-sm text-slate-600">Leva menos de um minuto.</p></div>);

export function LeadSection() {
  return (
    <section id="proposta" className="bg-[#F0F7FF] py-20 sm:py-28 scroll-mt-20" data-testid="lp-lead-section">
      <Container className="grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Fale com a DPdigi</Eyebrow>
          <H2>Descubra como a DPdigi pode cuidar do seu Departamento Pessoal.</H2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">Conte um pouco sobre sua empresa. Nossa equipe comercial entra em contato para entender suas necessidades e apresentar a proposta.</p>
          <ul className="mt-8 space-y-3 text-[15px] text-[#063C6D]">{["Plataforma digital com Jessy AI", "Admissões e contratos assinados pelo celular", "Folha processada pela equipe especializada"].map((t) => (
            <li key={t} className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#0B6FC2]" />{t}</li>))}</ul>
          <div className="mt-8"><DemoCta testid="lp-lead-demo-cta" /></div>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-7 rounded-[2rem] bg-white shadow-[0_30px_60px_-30px_rgba(6,60,109,0.45)] ring-1 ring-slate-200">
          <LeadForm ctx={{ source: "lead_form" }} tid="lead-inline" header={LeadHead} />
        </Reveal>
      </Container>
    </section>);
}
